import type { FleetManagementTypes } from '@nx/state';
import { isNonEmptyString, isNotNullish, isNullish } from '@nx/stdlib';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { hasLicenseIssues } from '../../../../../packages/state/src/slices/fleet-management/types';
import { tokenExpired, tokenExpirySoon } from '../deployments/token-management';
import { hasServersWithOutdatedPlugin } from '../shared/components/server-status';

export enum STATE_FILTERS {
  TOKEN_EXPIRY_SOON = 'Token expiry soon',
  TOKEN_EXPIRED = 'Token expired',
  LICENSE_ISSUE = 'License issue',
  OUTDATED_PLUGIN = 'Outdated plugin',
  HAS_CONNECTION_URL = 'Has connection URL',
}

export type DeploymentFilters = {
  search: string;
  status: string[];
  version: string[];
  edition: string[];
  packaging: string[];
  state: STATE_FILTERS[];
};

export type AvailableDeploymentFilters = {
  version: string[];
  edition: string[];
  packaging: string[];
};

const defaultFilters: DeploymentFilters = {
  search: '',
  status: [],
  version: [],
  edition: [],
  packaging: [],
  state: [],
};

type UseDeploymentFilters = (deployments: FleetManagementTypes.DeploymentDetails[]) => {
  filters: DeploymentFilters;
  availableFilters: AvailableDeploymentFilters;
  onFilterChange: (newValues: Partial<DeploymentFilters>) => void;
  clearAllFilters: () => void;
  hasActiveFilters: boolean;
  filteredDeployments: FleetManagementTypes.DeploymentDetails[];
};

const STORAGE_KEY = 'deploymentFilters';

const storeFilters = (newFilters: DeploymentFilters) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(newFilters));
  } catch (error) {
    /* empty */
  }
};

export const useDeploymentFilters: UseDeploymentFilters = (deployments: FleetManagementTypes.DeploymentDetails[]) => {
  const availableFilters = useMemo<AvailableDeploymentFilters>(() => {
    return {
      version: [...new Set(deployments.map((deployment) => deployment.dbms?.version).filter(isNotNullish))],
      edition: [...new Set(deployments.map((deployment) => deployment.dbms?.edition).filter(isNotNullish))],
      packaging: [...new Set(deployments.map((deployment) => deployment.dbms?.packaging).filter(isNotNullish))],
    };
  }, [deployments]);

  const initialFilters = useMemo<DeploymentFilters>(() => {
    try {
      const item = window.localStorage.getItem(STORAGE_KEY);
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const storedFilters = isNonEmptyString(item) ? (JSON.parse(item) as DeploymentFilters) : null;

      return storedFilters ? { ...defaultFilters, ...storedFilters } : defaultFilters;
    } catch (error) {
      return defaultFilters;
    }
  }, []);

  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    if (deployments.length === 0) {
      return;
    }
    // filter out values that are not in the available filters
    const filteredFilters = {
      ...filters,
      version: filters.version.filter((version) => availableFilters.version.includes(version)),
      edition: filters.edition.filter((edition) => availableFilters.edition.includes(edition)),
      packaging: filters.packaging.filter((packaging) => availableFilters.packaging.includes(packaging)),
    };

    setFilters(filteredFilters);
    // only run this effect when the available filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableFilters]);

  useEffect(() => {
    storeFilters(filters);
  }, [filters]);

  const onFilterChange = useCallback((newValues: Partial<DeploymentFilters>) => {
    setFilters((oldFilters) => ({
      ...oldFilters,
      ...newValues,
    }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return !isEqual({ ...filters, search: '' }, { ...defaultFilters, search: '' });
  }, [filters]);

  const filteredDeployments = useMemo(() => {
    return deployments.filter((deployment) => {
      if (filters.status.length > 0) {
        const deploymentStatus = deployment.status.toLowerCase();
        if (!filters.status.includes(deploymentStatus)) {
          return false;
        }
      }
      if (filters.version.length > 0) {
        if (!filters.version.includes(deployment.dbms?.version ?? '')) {
          return false;
        }
      }
      if (filters.edition.length > 0) {
        if (!filters.edition.includes(deployment.dbms?.edition ?? '')) {
          return false;
        }
      }
      if (filters.packaging.length > 0) {
        if (!filters.packaging.includes(deployment.dbms?.packaging ?? '')) {
          return false;
        }
      }
      if (filters.state.length > 0) {
        if (filters.state.includes(STATE_FILTERS.HAS_CONNECTION_URL)) {
          if (isNullish(deployment.connectionUrl)) {
            return false;
          }
        }
        if (filters.state.includes(STATE_FILTERS.TOKEN_EXPIRY_SOON)) {
          if (isNullish(deployment.token) || !tokenExpirySoon(deployment.token)) {
            return false;
          }
        }
        if (filters.state.includes(STATE_FILTERS.TOKEN_EXPIRED)) {
          if (isNullish(deployment.token) || !tokenExpired(deployment.token)) {
            return false;
          }
        }
        if (filters.state.includes(STATE_FILTERS.LICENSE_ISSUE)) {
          if (!hasLicenseIssues(deployment.dbms)) {
            return false;
          }
        }
        if (filters.state.includes(STATE_FILTERS.OUTDATED_PLUGIN)) {
          if (!hasServersWithOutdatedPlugin(deployment)) {
            return false;
          }
        }
      }
      return true;
    });
  }, [deployments, filters]);

  return {
    filters,
    availableFilters,
    onFilterChange,
    clearAllFilters,
    hasActiveFilters,
    filteredDeployments,
  };
};
